'use client';

import { useEffect, useRef, useState } from 'react';

const REVEAL_MS = 700;

/**
 * Minimal scroll-reveal wrapper for the /demo acts: fades + lifts content in
 * the first time it enters the viewport. CSS-only transition (no framer-motion)
 * and inert under prefers-reduced-motion via the motion-reduce utilities.
 *
 * After the lift finishes, the transform is dropped. A leftover
 * `translate-y-0` would still be a containing block, and sticky descendants
 * (the Explore section rail) would pin to this wrapper instead of the viewport.
 */
export const Reveal = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      setSettled(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || settled) return;
    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setSettled(true);
      return;
    }
    const t = window.setTimeout(() => setSettled(true), REVEAL_MS);
    return () => window.clearTimeout(t);
  }, [visible, settled]);

  return (
    <div
      ref={ref}
      className={
        settled
          ? className
          : `transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none ${
              visible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4 motion-reduce:opacity-100 motion-reduce:translate-y-0'
            } ${className}`
      }
    >
      {children}
    </div>
  );
};
